import { isNotDefined } from '@sgme/fp';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';

import { useOptionSelector } from '@/App/tiles/Option/OptionContext.ts';
import { useCheckOption } from '@/App/tiles/Option/QueueContext.tsx';
import { DatePickerWithTenorNext } from '@/components/ui/DatePickerWithTenorNext.tsx';
import { TradeCaptureValidationWrapper } from '@/components/ui/TradeCaptureFieldErrorWrapper.tsx';
import { selectOptionLegsValidation } from '@/store/option/selectors/optionSelectors.ts';
import type { LegId, OptionBarrier } from '@/store/option/state.ts';
import type { TcValidation } from '@/store/types.ts';

type TriggerStartDateProps = {
  tileId: string;
  barrier: OptionBarrier;
  numberOfBarrier?: number;
  legId: LegId;
  barrierValidation?: TcValidation[];
};

export function TriggerStartDate1({ tileId, legId }: { tileId: string; legId: LegId }) {
  const { barrier1, barrierValidation } = useOptionSelector((option) => {
    const leg = option.properties.legs.find((leg) => leg.legId === legId);
    const validation = selectOptionLegsValidation(option, legId);

    return {
      barrier1: leg?.barrier1,
      barrierValidation: validation?.barrier1?.startDate,
    };
  });

  if (isNotDefined(barrier1)) {
    return null;
  }

  return (
    <TriggerStartDate
      barrier={barrier1}
      tileId={tileId}
      numberOfBarrier={1}
      legId={legId}
      barrierValidation={barrierValidation}
    />
  );
}

export function TriggerStartDate2({ tileId, legId }: { tileId: string; legId: LegId }) {
  const { barrier2, barrierValidation } = useOptionSelector((option) => {
    const leg = option.properties.legs.find((leg) => leg.legId === legId);
    const validation = selectOptionLegsValidation(option, legId);

    return {
      barrier2: leg?.barrier2,
      barrierValidation: validation?.barrier2?.startDate,
    };
  });

  if (isNotDefined(barrier2)) {
    return null;
  }

  return (
    <TriggerStartDate
      barrier={barrier2}
      tileId={tileId}
      numberOfBarrier={2}
      legId={legId}
      barrierValidation={barrierValidation}
    />
  );
}

export function TriggerStartDate({
  tileId,
  barrier,
  numberOfBarrier = 1,
  legId,
  barrierValidation,
}: TriggerStartDateProps) {
  const [checkOption] = useCheckOption();

  const { sgwtWebAnalytics } = useSgwtWidgets();

  const handleBarrierStartDateSelected = (dateOrTenor: string) => {
    sgwtWebAnalytics?.trackEvent('Option', 'Trigger start date selected', dateOrTenor);

    checkOption({
      tileId,
      legProperties: {
        legId,
        [`barrier${numberOfBarrier}`]: {
          startDate: dateOrTenor,
        },
      },
    });
  };

  return (
    <TradeCaptureValidationWrapper validation={barrierValidation}>
      <DatePickerWithTenorNext
        data-e2e={`trigger-${numberOfBarrier}-start-date`}
        date={barrier.startDate}
        onChange={handleBarrierStartDateSelected}
      />
    </TradeCaptureValidationWrapper>
  );
}
